// src/hooks/useWakeLock.ts
// Mantiene la pantalla encendida mientras hay una changa activa
// Soporta Wake Lock API (Chrome, Edge) y fallback para Safari usando video invisible

import { useEffect, useRef } from 'react'

export function useWakeLock(active: boolean = false) {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if (!active) {
          // Liberar wake lock
          if (wakeLockRef.current) {
            await wakeLockRef.current.release()
            wakeLockRef.current = null
          }
          // Limpiar video de Safari si existe
          if (videoRef.current) {
            videoRef.current.pause()
            videoRef.current.remove()
            videoRef.current = null
          }
          return
        }

        // Intentar Wake Lock API primero (Chrome, Edge, Opera)
        if ('wakeLock' in navigator) {
          try {
            wakeLockRef.current = await navigator.wakeLock.request('screen')

            // Re-solicitar si la pantalla se apaga
            wakeLockRef.current.addEventListener('release', () => {
              wakeLockRef.current = null
            })
            return // Éxito con Wake Lock API
          } catch (err) {
            console.warn('[useWakeLock] ⚠️ Wake Lock API falló:', err)
          }
        }

        // Fallback para Safari/iOS: usar video invisible
        // Safari no soporta Wake Lock API pero podemos usar un video mudo invisible
        if (!videoRef.current) {
          const video = document.createElement('video')
          video.setAttribute('playsinline', '')
          video.setAttribute('muted', '')
          video.style.position = 'fixed'
          video.style.opacity = '0'
          video.style.pointerEvents = 'none'
          video.style.width = '1px'
          video.style.height = '1px'
          video.style.zIndex = '-1000'
          
          // Video vacío de 1 segundo en loop
          video.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAs1tZGF0AAACrgYF//+q3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1MiByMjg1NCBlOWE1OTAzIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNyAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTYgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTI1IHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAA'
          
          document.body.appendChild(video)
          videoRef.current = video
          
          // Iniciar reproducción
          video.loop = true
          video.play().catch(err => {
            console.warn('[useWakeLock] ⚠️ No se pudo reproducir video de Safari:', err)
          })
          
        }
      } catch (err) {
        console.warn('[useWakeLock] ⚠️ Error general:', err)
      }
    }

    requestWakeLock()

    return () => {
      // Cleanup
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {})
      }
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.remove()
        videoRef.current = null
      }
    }
  }, [active])
}
